import type { PortEnv, PhpRef } from '../../port-contract';
import { PhpArray, array_values, array_unique, array_merge, in_array, empty, intval, strval, strtolower, truthy, equal, phpCopy } from '../../php-compat';
import { at, isArray, values, entries, type Row, catalogGetId, htmlspecialchars as h } from './support';
import { AbilityFeature, UserChoiceType } from './catalog';
export interface TeamDisplayDeps {
    has_status_effect(target: Row, id: string | null): boolean;
    ability_entry_id(value: Row): unknown;
    ability_entry_name(value: Row): unknown;
    caster_class_level_map(item: Row): Row;
    layer_entries(item: Row, layer: string): Row;
    layer_value(item: Row, layer: string): unknown;
    layer_has(item: Row, layer: string): boolean;
    meet_ability_requirements(value: Row, item: Row, levels: Row): boolean;
    check_uuid(id: string): boolean;
    ft_name_from_blob(item: Row, id: string): unknown;
    icon_ph(name: unknown): string;
}
/** PHP: check_target_hidden (includes/functions/janus/core/team_and_display_helpers.php:457) */
export function checkTargetHidden(target: Row, env: PortEnv<TeamDisplayDeps>): boolean {
    return env.deps.has_status_effect(target, catalogGetId('StatusEffect', [{ variable_name: 'hidden', item_id: '47667406-1B72-9D60-310B-D8CF6BFE397D' }], 'hidden', env)) ? true : false;
}
/** PHP: display_ability_tree (includes/functions/janus/core/team_and_display_helpers.php:202) */
export function displayAbilityTree(ability: Row, item: Row, indent = 0, visited: PhpRef<Row> = { value: [] }, env: PortEnv<TeamDisplayDeps>): Row {
    indent = Number(intval(indent));
    const d = env.deps, id = d.ability_entry_id(ability);
    let result: Row = [];
    if (empty(id) || in_array(id, visited.value)) return result;
    const seen = new PhpArray(entries(phpCopy(visited.value))); seen.append(id); visited.value = seen;
    const levels = d.caster_class_level_map(item);
    result = [{ ability_id: id, item_name: d.ability_entry_name(ability), indent_level: indent,
        indent_style: indent > 0 ? `style="padding-left: ${strval(indent * 1.25)}rem;"` : '', arrow: indent > 0 ? '⤷ ' : '' }];
    const childTypes = array_values(array_unique(array_merge(AbilityFeature, UserChoiceType.filter(t => !['damage_type_resistance', 'damage_type_immunity', 'size', 'environment', 'creature_type'].includes(t)))));
    for (const type of values(childTypes)) {
        const list = d.layer_entries(item, strval(type));
        if (empty(list)) continue;
        for (const child of values(list)) {
            if (!isArray(child)) continue;
            const link = !empty(at(child, 'choice_item_id')) ? 'choice_item_id' : !empty(at(child, 'exact_choice_item_id')) ? 'exact_choice_item_id' : !empty(at(child, 'source')) ? 'source' : '';
            if (link && (at(child, link) ?? '') === id) {
                if (!d.meet_ability_requirements(child, item, levels)) continue;
                result = array_merge(result, displayAbilityTree(child, item, indent + 1, visited, env));
            }
        }
    }
    const choices = at(item, 'user_choices');
    if (isArray(choices)) {
        const key = strtolower(strval(id));
        for (const choice of values(choices)) {
            if (!isArray(choice)) continue;
            const uuid = strval(at(choice, 'choice') ?? '');
            if (uuid === '' || !d.check_uuid(uuid)) continue;
            const child = ['exact_choice_item_id', 'choice_item_id'].some(col => { const parent = strval(at(choice, col) ?? ''); return parent !== '' && strtolower(parent) === key; });
            if (!child) continue;
            const nest = { ability_id: uuid, item_name: at(choice, 'choice_shell', 'items', 'item_name') ?? d.ft_name_from_blob(item, uuid),
                source: strval(at(choice, 'exact_choice_item_id') ?? at(choice, 'choice_item_id') ?? ''),
                choice_item_id: strval(at(choice, 'choice_item_id') ?? ''), exact_choice_item_id: strval(at(choice, 'exact_choice_item_id') ?? '') };
            if (!d.meet_ability_requirements(nest, item, levels)) continue;
            result = array_merge(result, displayAbilityTree(nest, item, indent + 1, visited, env));
        }
    }
    return result;
}
/** PHP: display_spell_casting_time (includes/functions/janus/core/team_and_display_helpers.php:332) */
export function displaySpellCastingTime(item: Row, env: PortEnv<TeamDisplayDeps>): string {
    const time = env.deps.layer_value(item, 'spell_casting_time');
    for (const code of ['1ap', '2ap', '3ap', 'reaction']) if (equal(time, code)) return env.deps.icon_ph(code);
    for (const [code, label] of Object.entries({ one_minute: '1 Minute', ten_minutes: '10 Minutes', one_hour: '1 Hour', eight_hours: '8 Hours', twelve_hours: '12 Hours', twenty_four_hours: '24 Hours' }))
        if (equal(time, code)) return env.deps.icon_ph('3ap') + ` <span class='badge bg-dark border'>${label}</span>`;
    return '';
}
/** PHP: get_spell_time_display (includes/functions/janus/core/team_and_display_helpers.php:366) */
export function getSpellTimeDisplay(time: unknown): string {
    for (const [code, label] of Object.entries({ one_minute: '1 Min', ten_minutes: '10 Min', one_hour: '1 Hr', eight_hours: '8 Hrs', twelve_hours: '12 Hrs', twenty_four_hours: '24 Hrs' })) if (equal(time, code)) return label;
    return '';
}
/** PHP: get_spell_duration_display (includes/functions/janus/core/team_and_display_helpers.php:394) */
export function getSpellDurationDisplay(item: Row, short = false, env: PortEnv<TeamDisplayDeps>): string {
    const d = env.deps, hasInt = d.layer_has(item, 'duration_int'), hasType = d.layer_has(item, 'duration_type');
    const type = strval(d.layer_value(item, 'duration_type') ?? ''), int = d.layer_value(item, 'duration_int');
    if (hasInt && !hasType) return intval(int) === 0 ? 'Instant' : '';
    if (type === 'permanent') return d.icon_ph('duration_permanent');
    if (hasInt && hasType) {
        const full: Record<string, [string, string]> = { seconds: ['Second', 'Seconds'], hours: ['Hour', 'Hours'], minutes: ['Minute', 'Minutes'], days: ['Day', 'Days'], weeks: ['Week', 'Weeks'], months: ['Month', 'Months'], years: ['Year', 'Years'], rounds: ['Round', 'Rounds'] };
        const brief: Record<string, [string, string]> = { seconds: ['sec', 'secs'], hours: ['hr', 'hrs'], minutes: ['min', 'mins'], days: ['day', 'days'], weeks: ['week', 'weeks'], months: ['month', 'months'], years: ['year', 'years'], rounds: ['round', 'rounds'] };
        const label = (truthy(short) ? brief : full)[type]?.[intval(int) === 1 ? 0 : 1] ?? '';
        return h(strval(int), 3) + (label !== '' ? ' ' + label : '');
    }
    return '';
}
/** PHP: fate_tide_spell_row_supporting_columns_html (includes/functions/janus/core/team_and_display_helpers.php:471) */
export function fateTideSpellRowSupportingColumnsHtml(spell: Row, env: PortEnv<TeamDisplayDeps>): Row {
    const d = env.deps;
    const range = d.layer_has(spell, 'range') ? d.icon_ph(d.layer_value(spell, 'range')) : '';
    const aoe = d.layer_value(spell, 'aoe_type') !== null ? d.icon_ph(d.layer_value(spell, 'aoe_type')) + "<br /><span class='badge bg-dark border'>" + h(strval(d.layer_value(spell, 'aoe_distance') ?? ''), 3) + '</span>' : '—';
    let duration = getSpellDurationDisplay(spell, true, env);
    if (d.layer_has(spell, 'spell_concentration')) duration += '<br />' + d.icon_ph('concentration');
    const hit = d.layer_has(spell, 'skill_roll_contestee') ? d.icon_ph(d.layer_value(spell, 'skill_roll_contestee')) : d.layer_has(spell, 'roll_type') ? d.icon_ph(d.layer_value(spell, 'roll_type')) : '';
    const cast = d.layer_value(spell, 'spell_casting_time') !== null ? getSpellTimeDisplay(d.layer_value(spell, 'spell_casting_time')) : '';
    return { range, aoe, duration, hit_dc: hit, cast_time: cast };
}
